import { useQuery } from "@tanstack/react-query";
import type { Location } from "@components/ui/location_input";
import type { Option } from "@sprout/select";
import { CompletionStatusType } from "@utils/completion_status";
import { DoNotSellType } from "@utils/do_not_sell";
import { Education } from "@components/quick_apply/education_section";
import { Employment } from "@components/quick_apply/employments";
import { Websites } from "@components/quick_apply/websites";
import { SelfIdentificationProps } from "@/utils/self_identification";

export interface FileState {
  id?: string;
  file: File | null;
  filename: string;
  url: string;
  content_type: string;
}

export interface ProfileData {
  email: string;
  firstName: string | null;
  lastName: string | null;
  isIdentityVerified: boolean;
  identityVerificationPartner: string | null;
  hasPendingIdentityVerificationTask: boolean;
  pendingIdentityVerificationTaskPartner: string | null;
  phoneNumber: string | null;
  preferredName: string | null;
  bio: string | null;
  skills: string[];
  location: Location | null;
  resume: FileState | null;
  educations: Education[];
  employments: Employment[];
  websites: Websites;
  accountCompletion: CompletionStatusType;
  quickApplyCompletion: CompletionStatusType;
  workPreferencesCompletion: CompletionStatusType;
  workPreferences: {
    employmentTypes: string[];
    worksites: string[];
    location: string | null;
    languages: string[];
    willingToRelocate: boolean | null;
    salary: string | null;
    desiredJobTitles: string[];
  };
  workPreferencesForm: {
    employmentTypes: Option[];
    worksites: Option[];
    currentLanguages: Option[];
    currentEmploymentTypes: Option[];
    currentWorksites: Option[];
    currentRelocation: boolean | null;
    currentLocation: Location | null;
    currentSalary: string | null;
    currentDesiredJobTitles: string[];
    paths: { workPreferencesPath: string };
  };
  doNotSell: DoNotSellType | null;
  paths: {
    userPath: string;
    accountPath: string;
    quickApplyPath: string;
    educationPath: string;
    doNotSharePath: string;
    resumeUploadPath: string;
  };
  selfIdentification: SelfIdentificationProps;
  userProfileEnabled: boolean;
}

const useProfile = () => {
  const fetchProfile = async (): Promise<ProfileData> => {
    const response = await fetch("/profiles/data.json");
    if (!response.ok) {
      throw new Error("Failed to fetch profile data");
    }
    return response.json();
  };

  return useQuery({
    queryKey: ["profile"],
    queryFn: fetchProfile,
    staleTime: 0,
  });
};

export default useProfile;
